// src/pages/createsign.tsx  (or ExternalSignatureCreator.tsx)
import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

type SignatureStyle =
  | "bold"
  | "cursive"
  | "minimal"
  | "elegant"
  | "handwritten"
  | "modern"
  | "stamp"
  | "mono"
  | "calligraphy";

const signatureStyles: Record<SignatureStyle, React.CSSProperties> = {
  bold: { fontFamily: "'Arial Black', sans-serif", fontWeight: 900, fontSize: "46px" },
  cursive: { fontFamily: "'Brush Script MT', cursive", fontSize: "56px" },
  minimal: { fontFamily: "'Helvetica Neue', sans-serif", fontWeight: 300, fontSize: "48px" },
  elegant: { fontFamily: "'Georgia', serif", fontStyle: "italic", fontSize: "50px" },
  handwritten: { fontFamily: "'Dancing Script', cursive", fontSize: "58px" },
  modern: { fontFamily: "'Montserrat', sans-serif", fontWeight: 600, fontSize: "46px" },
  stamp: { fontFamily: "'Impact', sans-serif", fontWeight: 900, fontSize: "44px" },
  mono: { fontFamily: "'Courier New', monospace", fontSize: "46px" },
  calligraphy: { fontFamily: "'Great Vibes', cursive", fontSize: "60px" },
};

interface Props {
  token?: string; // optional if used internally too
  onSignatureCreated: (signatureImageUrl: string) => void;
}

const ExternalSignatureCreator: React.FC<Props> = ({ token = "", onSignatureCreated }) => {
    const navigate = useNavigate();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [style, setStyle] = useState<SignatureStyle>("cursive");
  const [selectedSignature, setSelectedSignature] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const generateSignatures = (): string[] => {
    if (!fullName.trim()) return [];
    const words = fullName.trim().split(" ");
    const first = words[0];
    const last = words.slice(-1)[0];
    const fi = first[0];
    const li = last[0];

    return [
      fullName,
      `${first} `,
      `${fi}. ${last}`,
      `${first} ${li}.`,
      `${fi}.${li}.`,
      fullName.toUpperCase(),
    ].filter(Boolean);
  };

// Convert text to a PNG blob
const textToImageBlob = async (text: string): Promise<Blob> => {
  const canvas = document.createElement("canvas");
  canvas.width = 700;
  canvas.height = 220;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas context not available");

  ctx.fillStyle = "white";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "black";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  const fontStyle = signatureStyles[style];
  ctx.font = `${fontStyle.fontStyle || "normal"} ${fontStyle.fontWeight || "normal"} ${fontStyle.fontSize} ${fontStyle.fontFamily}`;
  ctx.fillText(text, canvas.width / 2, canvas.height / 2 + 8);

  // Use toBlob if available
  if (canvas.toBlob) {
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Blob creation failed"));
      }, "image/png");
    });
  }

  // Fallback for environments without toBlob
  const dataUrl = canvas.toDataURL("image/png");
  const response = await fetch(dataUrl);
  return await response.blob();
};

// Save signature to backend
const saveSignature = async () => {
  if (!selectedSignature || !fullName.trim()) {
    alert("Please enter your name and select a signature");
    return;
  }

  setIsSaving(true);

  try {
    // Convert signature to blob
    const blob = await textToImageBlob(selectedSignature);

    const formData = new FormData();
    formData.append("file", blob, "signature.png");
    formData.append("fullName", fullName.trim());
    formData.append("email", email.trim() || "external@sign.com");
    formData.append("style", style);
    if (token) formData.append("token", token);

    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

    const res = await axios.post(`${API_URL}/api/save-external-signature`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    console.log("SAVE RESPONSE:", res.data);

    if (res.data?.success && res.data.signatureUrl) {
      alert("Signature saved successfully!");

      // THIS LINE WAS MISSING — THIS FIXES EVERYTHING!
      onSignatureCreated(res.data.signatureUrl);
      
    } else {
      console.warn("Signature saved, but no URL returned:", res.data);
      alert("Signature saved, but could not retrieve URL.");
    }

  } catch (err: any) {
    console.error("Save failed:", err.response?.data || err.message);
    alert("Error: " + (err.response?.data?.error || err.message || "Failed to save"));
  } finally {
    setIsSaving(false);
  }
};



  const signatures = generateSignatures();

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", fontFamily: "'Segoe UI', sans-serif" }}>
      {/* BLUE HEADER WITH NAV BUTTONS */}
      <header style={{
        background: "linear-gradient(90deg, #003f7d, #005eb8)",
        color: "white",
        padding: "60px 40px 100px",
        textAlign: "center"
      }}>
        <h1 style={{ fontSize: "2.5rem", margin: "0 0 12px 0", fontWeight: "700" }}>
          Digital Document Signing System
        </h1>
        <p style={{ fontSize: "1.3rem", margin: "0 0 40px 0", opacity: 0.9, letterSpacing: "1px" }}>
          FAST • SECURE • PAPERLESS
        </p>

        {/* NAVIGATION PILLS */}
        <div style={{ display: "flex", justifyContent: "center", gap: "16px", flexWrap: "wrap" }}>
          <button  onClick={() => navigate("/Home")}style={{ padding: "10px 24px", borderRadius: "30px", border: "none", background: "rgba(255,255,255,0.25)", color: "white", fontWeight: "500", cursor: "pointer" }}>Dashboard</button>
          <button style={{ padding: "12px 28px", borderRadius: "30px", border: "none", background: "white", color: "#003f7d", fontWeight: "600", cursor: "pointer", boxShadow: "0 4px 15px rgba(0,0,0,0.1)" }}>Create Signature</button>
          <button  onClick={() => navigate("/viewsign")}style={{ padding: "10px 24px", borderRadius: "30px", border: "none", background: "rgba(255,255,255,0.25)", color: "white", fontWeight: "500", cursor: "pointer" }}>View Signature</button>
          <button  onClick={() => navigate("/UploadSign")}style={{ padding: "10px 24px", borderRadius: "30px", border: "none", background: "rgba(255,255,255,0.25)", color: "white", fontWeight: "500", cursor: "pointer" }}>Upload & Sign</button>
          <button onClick={() => navigate("/documents")} style={{ padding: "10px 24px", borderRadius: "30px", border: "none", background: "rgba(255,255,255,0.25)", color: "white", fontWeight: "500", cursor: "pointer" }}>View Documents</button>
        </div>
      </header>

      {/* WHITE CARD - EXACT MATCH */}
      <div style={{
        maxWidth: "900px",
        margin: "-80px auto 80px",
        background: "white",
        borderRadius: "30px",
        boxShadow: "0 20px 60px rgba(0,0,0,0.12)",
        padding: "60px 80px",
        position: "relative",
        zIndex: 10
      }}>
        <h2 style={{ fontSize: "2rem", color: "#003f7d", margin: "0 0 50px 0", textAlign: "left", fontWeight: "700" }}>
          Create Your Digital Signature
        </h2>

        {/* Name */}
        <div style={{ marginBottom: "32px" }}>
          <label style={{ display: "block", marginBottom: "10px", color: "#003f7d", fontWeight: "600" }}>Name</label>
          <input
            type="text"
            placeholder="Enter full name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            style={{
              width: "100%",
              padding: "18px 20px",
              fontSize: "1.1rem",
              border: "1px solid #e0e0e0",
              borderRadius: "12px",
              outline: "none",
              background: "#fafafa"
            }}
          />
        </div>

        {/* Email */}
        <div style={{ marginBottom: "32px" }}>
          <label style={{ display: "block", marginBottom: "10px", color: "#003f7d", fontWeight: "600" }}>Email</label>
          <input
            type="email"
            placeholder="Enter email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{
              width: "100%",
              padding: "18px 20px",
              fontSize: "1.1rem",
              border: "1px solid #e0e0e0",
              borderRadius: "12px",
              outline: "none",
              background: "#fafafa"
            }}
          />
        </div>

        {/* Style */}
        <div style={{ marginBottom: "50px" }}>
          <label style={{ display: "block", marginBottom: "10px", color: "#003f7d", fontWeight: "600" }}>Signature Style</label>
          <select
            value={style}
            onChange={(e) => setStyle(e.target.value as SignatureStyle)}
            style={{
              width: "100%",
              padding: "18px 20px",
              fontSize: "1.1rem",
              border: "1px solid #e0e0e0",
              borderRadius: "12px",
              background: "#fafafa",
              outline: "none",
              cursor: "pointer"
            }}
          >
            {Object.keys(signatureStyles).map((key) => (
              <option key={key} value={key}>
                {key.charAt(0).toUpperCase() + key.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* PREVIEW GRID */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "24px",
          marginBottom: "60px"
        }}>
          {signatures.length === 0 ? (
            <div style={{ gridColumn: "1 / -1", textAlign: "center", color: "#999", fontSize: "1.2rem", padding: "40px" }}>
              Type your name to see signature previews
            </div>
          ) : (
            signatures.map((sig, i) => (
              <div
                key={i}
                onClick={() => setSelectedSignature(sig)}
                style={{
                  padding: "40px 20px",
                  textAlign: "center",
                  background: selectedSignature === sig ? "#e6f0ff" : "white",
                  border: selectedSignature === sig ? "2px solid #0000" : "1px solid #e0e0e0",
                  borderRadius: "16px",
                  cursor: "pointer",
                  transition: "all 0.3s"
                }}
              >
                <div style={{ ...signatureStyles[style], color: "#010911ff" }}>
                  {sig}
                </div>
              </div>
            ))
          )}
        </div>

        {/* SAVE BUTTON */}
        <button
          onClick={saveSignature}
          disabled={!selectedSignature || isSaving || !fullName.trim()}
          style={{
            width: "100%",
            padding: "20px",
            fontSize: "1.4rem",
            fontWeight: "bold",
            color: "white",
            background: selectedSignature && !isSaving ? "#003f7d" : "#a0a0a0",
            border: "none",
            borderRadius: "14px",
            cursor: selectedSignature && !isSaving ? "pointer" : "not-allowed",
            boxShadow: "0 8px 25px rgba(0,63,125,0.3)"
          }}
        >
          {isSaving ? "Saving Signature..." : "Save & Continue"}
        </button>
      </div>
    </div>
  );
};

export default ExternalSignatureCreator;
